import { ScheduledMeeting } from "./dummyData";
import { deleteInterview, listInterview } from "@endpoints/interview.service";
import { groupedGeneralMeetings } from "../SchedulingSection/SchedulingSection";
import { useEffect, useMemo, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { isObjectEmpty } from "@utils/helperFunction";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  IGetInterviewByIdResponse,
  Meeting,
} from "@endpoints/types/interview.type";
import store from "@store/index";
import { EnumUserTypes } from "@utils/constants";

export function getMeetingStatus(item: IGetInterviewByIdResponse) {
  if (
    item?.interviewStatus !== "Declined" &&
    item?.slotSelectionType === "general"
      ? !item?.isCandidateConfirm && !item?.isCompanyConfirm
      : !item?.isCandidateConfirm && !item?.isRecruiterAgencyConfirm
  ) {
    return {
      label: "Action Required",
      textStyle: "text-orange-400",
      buttonStyle: "border-orange-400",
    };
  } else if (
    item?.interviewStatus !== "Declined" &&
    item?.slotSelectionType === "general"
      ? item?.isCandidateConfirm && item?.isCompanyConfirm
      : item?.isCandidateConfirm && item?.isRecruiterAgencyConfirm
  ) {
    return {
      label: "Accepted",
      textStyle: "text-green-500",
      buttonStyle: "border-green-500",
    };
  } else if (
    item?.interviewStatus !== "Declined" &&
    item?.slotSelectionType === "general"
      ? item?.isCompanyConfirm || item?.isRecruiterAgencyConfirm
      : item?.isRecruiterAgencyConfirm || item?.isCandidateConfirm
  ) {
    return {
      label: "Pending",
      textStyle: "text-blue-500",
      buttonStyle: "border-blue-500",
    };
  } else if (item?.interviewStatus === "Declined") {
    return {
      label: "Declined",
      textStyle: "text-red-500",
      buttonStyle: "border-red-500",
    };
  }
}

const ScheduleCard = ({ item, index }) => {
  const navigate = useNavigate();
  const isHiringCompanyInvolved = !!item?.hiringCompanyId?._id;
  const attendees = isHiringCompanyInvolved ? ["RA", "CA", "HC"] : ["RA", "CA"];
  const buttonInfo = getMeetingStatus(item);

  return (
    <div
      onClick={() => navigate(`/meeting-details?id=${item?._id}`)}
      className={`items-center p-3 grid grid-cols-[1fr_1fr_1fr_1fr_0.7fr] cursor-pointer hover:bg-gray-50 ${
        index !== 0 ? "border-t" : ""
      }`}
    >
      <span className={`font-medium text-light-black text-xs`}>
        {dayjs(item?.createdAt).format("Do")}&nbsp;&nbsp;
        {dayjs(item?.createdAt).format("dddd")}{" "}
        {dayjs(item?.createdAt).format("hh:mm a")}
      </span>
      <span className="font-medium text-xs text-primary flex-1">
        {item?.meetingTitle}
      </span>
      <span className="font-medium text-xs text-light-black flex-1">
        {item?.jobTitle}
      </span>
      <div className="flex flex-1 items-center gap-2">
        {attendees?.map((item) => {
          return (
            <div
              key={item}
              className="flex items-center justify-center h-8 w-8 text-xs font-bold border rounded-full border-light-grey text-light-black"
            >
              {item}
            </div>
          );
        })}
      </div>
      {/* Status button container */}
      <div
        className={`rounded-md flex items-center justify-center px-2.5 font-medium w-max py-1 text-xs border-[1.2px] ${buttonInfo?.textStyle} ${buttonInfo?.buttonStyle}`}
      >
        {buttonInfo?.label}
      </div>
    </div>
  );
};

const ScheduleSection = () => {
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [schedulingPolls, setSchedulingPolls] = useState({});

  const groupedMeetings = ScheduledMeeting.reduce((acc, meeting) => {
    const year = dayjs(meeting.meetingDate).format("YYYY");
    const month = dayjs(meeting.meetingDate).format("MMMM");

    if (!acc[year]) {
      acc[year] = {};
    }
    if (!acc[year][month]) {
      acc[year][month] = [];
    }
    acc[year][month].push(meeting);

    return acc;
  }, {});

  useEffect(() => {
    listInterview({
      isSchedule: true,
    })
      .then((response) => {
        const { success, data } = response;
        if (success) {
          // data?.map((item) => {
          //   deleteInterview(item?._id);
          // });
          setSchedulingPolls(() => groupedGeneralMeetings(data));
          // setEvents(() => formatEvents(data));
        }
      })
      .finally(() => setIsEventLoading(false));
  }, []);

  return (
    <div className="items-center w-full">
      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_0.7fr] w-full border-b p-3 mb-4">
        <span className="font-semibold text-xs text-gray-500">Date/Time</span>
        <span className="font-semibold text-xs text-gray-500">
          Meeting title
        </span>
        <span className="font-semibold text-xs text-gray-500">
          Position title
        </span>
        <span className="font-semibold text-xs text-gray-500">Attendees</span>
        <span className="font-semibold text-xs text-gray-500">Status</span>
      </div>
      {isEventLoading ? (
        <div className="flex items-center justify-center flex-1 flex-col">
          <LoadingAnimation color="#2e2e2e" />
        </div>
      ) : isObjectEmpty(schedulingPolls) ? (
        <div className="font-medium text-light-black text-sm h-[calc(100vh-300px)] flex flex-col justify-center items-center">
          No scheduling poll found.
        </div>
      ) : (
        <div className="flex-grow">
          {Object.keys(schedulingPolls).map((year, yearIndex) => (
            <div key={year} className="flex flex-col gap-10">
              {Object.keys(schedulingPolls[year]).map((month, monthIndex) => (
                <div key={month}>
                  <div
                    className={`flex items-center py-1.5 px-3 gap-4 bg-[#F1F5F9] rounded-sm`}
                  >
                    <label className="font-medium text-xs">
                      {month} {year}
                    </label>
                  </div>
                  {/* List of scheduled meetings */}
                  <div className="flex flex-col mt-2">
                    {schedulingPolls[year][month].map((meeting, index) => (
                      <ScheduleCard key={index} item={meeting} index={index} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* <div className="flex-grow">
        {Object.keys(groupedMeetings).map((year, yearIndex) => (
          <div key={year} className="flex flex-col gap-10">
            {Object.keys(groupedMeetings[year]).map((month, monthIndex) => (
              <div key={month}>
                <div
                  className={`flex items-center py-1.5 px-3 gap-4 bg-[#F1F5F9] rounded-sm`}
                >
                  <label className="font-medium text-sm">
                    {month} {year}
                  </label>
                </div>
                List of scheduled meetings
                <div className="flex flex-col mt-4">
                  {groupedMeetings[year][month].map((meeting, index) => (
                    <ScheduleCard key={index} item={meeting} index={index} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div> */}
    </div>
  );
};

export default ScheduleSection;
